import { FC, useCallback, useEffect, useState } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
} from '@chakra-ui/react';

import { Sku } from '@app/api/models/sku';
import { Dialog, Txt } from '@app/components/common';
import { TableBase } from '@app/components/common/table/table-base';
import { useApi } from '@app/hooks';

import { useTreeColumns } from './tree-columns';
import { SkuForm } from './form';
import { SkuLookups, SkuTreePathItem, SkuTreeRow, SkuViewMode } from './types';
import { SkuViewModeToolbar } from './view-mode-toolbar';

const SKU_PAGE_LIMIT = 500;

export const SkuTree: FC<{
  lookups: SkuLookups;
  canChange: boolean;
  mode: SkuViewMode;
  onModeChange: (mode: SkuViewMode) => void;
}> = ({ lookups, canChange, mode, onModeChange }) => {
  const api = useApi();

  const [path, setPath] = useState<SkuTreePathItem[]>([]);
  const [rows, setRows] = useState<SkuTreeRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<Partial<Sku>>();
  const [open, setOpen] = useState(false);

  const loadLevel = useCallback(
    async (nextPath: SkuTreePathItem[]) => {
      const last = nextPath[nextPath.length - 1];

      if (!last) {
        setRows(
          lookups.categories.map((category) => ({
            id: category.id,
            name: category.name,
            kind: 'category',
            is_active: category.is_active,
            category,
          }))
        );
        return;
      }

      if (last.kind === 'category') {
        setRows(
          lookups.subcategories
            .filter((subcategory) => subcategory.category_id === last.id)
            .map((subcategory) => ({
              id: subcategory.id,
              name: subcategory.name,
              kind: 'subcategory',
              is_active: subcategory.is_active,
              subcategory,
            }))
        );
        return;
      }

      setLoading(true);
      const response = await api.sku.getSkus({
        limit: SKU_PAGE_LIMIT,
        offset: 0,
        sort: '+title',
        filter: { subcategory_id: [last.id] },
      });
      setRows(
        (response?.data?.data || []).map((sku) => ({
          id: sku.id,
          name: sku.title,
          kind: 'sku',
          is_active: sku.is_active,
          sku,
        }))
      );
      setLoading(false);
    },
    [api, lookups]
  );

  useEffect(() => {
    loadLevel(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookups]);

  const goToLevel = useCallback(
    (index: number) => {
      const nextPath = path.slice(0, index);
      setPath(nextPath);
      loadLevel(nextPath);
    },
    [path, loadLevel]
  );

  const onRowClick = useCallback(
    (row: SkuTreeRow) => {
      if (row.kind === 'sku') {
        setSelected(row.sku);
        setOpen(true);
        return;
      }
      const nextPath: SkuTreePathItem[] = [
        ...path,
        { kind: row.kind, id: row.id, name: row.name },
      ];
      setPath(nextPath);
      loadLevel(nextPath);
    },
    [path, loadLevel]
  );

  const onEdit = useCallback((row: SkuTreeRow) => {
    setSelected(row.sku);
    setOpen(true);
  }, []);

  const onCreate = useCallback(() => {
    setSelected({
      category_id: path[0]?.id,
      subcategory_id: path[1]?.id,
      is_active: true,
    });
    setOpen(true);
  }, [path]);

  const columns = useTreeColumns({ lookups, onEdit, canChange });

  const isSkuLevel = path[path.length - 1]?.kind === 'subcategory';

  return (
    <>
      <TableBase<SkuTreeRow>
        title={
          <Flex gap={2} alignItems="center">
            <Txt>Управление SKU</Txt>
            <SkuViewModeToolbar mode={mode} onChange={onModeChange} />
          </Flex>
        }
        secondaryToolbar={
          <Breadcrumb spacing={1} separator="/">
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => goToLevel(0)}>Все категории</BreadcrumbLink>
            </BreadcrumbItem>
            {path.map((item, index) => (
              <BreadcrumbItem key={`${item.kind}_${item.id}`} isCurrentPage={index === path.length - 1}>
                <BreadcrumbLink onClick={() => goToLevel(index + 1)}>{item.name}</BreadcrumbLink>
              </BreadcrumbItem>
            ))}
          </Breadcrumb>
        }
        columns={columns}
        data={rows}
        loading={loading}
        onRowClick={(row) => onRowClick(row as SkuTreeRow)}
        onCreateClick={canChange && isSkuLevel ? onCreate : undefined}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={selected && 'id' in selected && selected.id ? 'Редактирование SKU' : 'Создание SKU'}
        width={720}
        footerless
      >
        {open && (
          <SkuForm
            initial={selected}
            lookups={lookups}
            onSubmit={() => {
              setOpen(false);
              loadLevel(path);
            }}
            onCancel={() => setOpen(false)}
          />
        )}
      </Dialog>
    </>
  );
};

export default SkuTree;
